/**
 * Выбор заметки, в которую переносят фотографию.
 *
 * ПОЧЕМУ НЕ `select`. Выпадающий список умеет ровно одну вещь — показать строку текста, — а
 * решение «в какую из двадцати» человек принимает не по тексту: заметки без слов неразличимы
 * по подписи вовсе, и список из пяти «без описания» подряд заставляет угадывать. Кадр
 * опознаётся кадром. Поэтому строка несёт миниатюру, дату, счёт фотографий и знак места — то
 * есть ровно то, по чему заметку узнают в панели, и той же формой.
 *
 * `radiogroup`, А НЕ СПИСОК КНОПОК: выбирают ОДНУ из закрытого набора, и это же говорит
 * скринридеру, сколько их всего и которая отмечена. Текущая заметка отмечена и не нажимается:
 * перенести фотографию туда, где она и так лежит, — это нажатие, которое ничего не делает, и
 * человек пойдёт искать поломку там, где её нет.
 *
 * ПОТОЛОК 20 — это весь список, а не его начало: больше заметок на поле не бывает.
 */

import { useEffect, useRef, useState } from "react";

import type { FieldMarker, FieldPhoto } from "../api/types";
import { formatFull } from "../lib/format";
import { hasPlace, photosOfNote, sortNotes } from "../lib/notes";
import { T } from "../strings";
import { Glyph, glyph } from "./DrawTools";

/** Сколько заметок помещается в список. Ровно потолок заметок на поле. */
export const PICKER_CAP = 20;

interface Props {
  notes: FieldMarker[];
  /** Фотографии ВСЕГО поля: из них берётся и счёт строки, и её миниатюра. */
  photos: FieldPhoto[];
  /** Где фотография лежит сейчас — эта строка отмечена и не нажимается. */
  currentId: number;
  onPick: (noteId: number) => void;
  /** Откуда брать миниатюру. Нет источника — строки остаются без кадра, но не ломаются. */
  thumbUrl?: (photoId: number) => Promise<string>;
  busy?: boolean;
}

/**
 * Обложки строк: первый кадр каждой заметки.
 *
 * Тянется СРАЗУ и всё: список открывают явным нажатием, он короткий (двадцать строк — предел
 * поля), и весь он на экране. Ленивость здесь стоила бы наблюдателя ради двадцати запросов,
 * которые всё равно уйдут. Отзыв — на размонтировании, одним проходом.
 */
const useCovers = (
  covers: FieldPhoto[],
  load: ((photoId: number) => Promise<string>) | undefined,
): Map<number, string> => {
  const urls = useRef(new Map<number, string>());
  const asked = useRef(new Set<number>());
  const [, bump] = useState(0);
  /**
   * ЖИВ ЛИ КОМПОНЕНТ — РЕФОМ, А НЕ ПЕРЕМЕННОЙ ЭФФЕКТА, и это не стиль, а тот самый дефект.
   *
   * Первая версия держала `let live = true` внутри эффекта и гасила его уборкой. Но список
   * кадров — свежий массив на каждый рендер, значит эффект перезапускался на каждом, и уборка
   * ПРЕДЫДУЩЕГО прогона гасила `live` для запроса, который ещё летел. Блоб приезжал в мёртвое
   * замыкание, отзывался на месте и не доходил до карты никогда: обложки оставались пустыми
   * при полностью исправной загрузке.
   *
   * Реф живёт столько же, сколько компонент, и гаснет ровно один раз — при размонтировании.
   */
  const alive = useRef(true);
  // Источник кадров приходит стрелкой из родителя и меняет личность каждый рендер; в
  // зависимостях ему делать нечего — иначе всё возвращается к перезапуску на каждом рендере.
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    alive.current = true;
    const held = urls.current;
    return () => {
      alive.current = false;
      for (const url of held.values()) URL.revokeObjectURL(url);
      held.clear();
    };
  }, []);

  // Ключ по ИДЕНТИФИКАТОРАМ, а не по массиву: пересобранный список тех же кадров — это тот же
  // список, и перезапускать из-за него нечего.
  const key = covers.map((photo) => photo.id).join(",");
  useEffect(() => {
    const get = loadRef.current;
    if (!get) return;
    for (const id of key === "" ? [] : key.split(",").map(Number)) {
      if (asked.current.has(id)) continue;
      asked.current.add(id);
      void get(id).then(
        (url) => {
          if (!alive.current) {
            URL.revokeObjectURL(url);
            return;
          }
          urls.current.set(id, url);
          bump((token) => token + 1);
        },
        () => {
          // Кадр не пришёл — строка остаётся с пустой рамкой. Дата, текст и счёт на месте,
          // выбрать по ним всё ещё можно, и сообщать тут не о чем.
        },
      );
    }
  }, [key]);

  return urls.current;
};

export function NotePicker({ notes, photos, currentId, onPick, thumbUrl, busy = false }: Props) {
  const shown = sortNotes(notes).slice(0, PICKER_CAP);
  const mine = shown.map((note) => photosOfNote(photos, note.id));
  const covers = mine.map((list) => list[0]).filter((photo): photo is FieldPhoto => !!photo);
  const urls = useCovers(covers, thumbUrl);

  return (
    <div
      className="k-picker"
      role="radiogroup"
      aria-label={T.photos.attachLabel}
      data-testid="photo-attach"
    >
      {shown.map((note, index) => {
        const own = mine[index] ?? [];
        const cover = own[0];
        const url = cover === undefined ? undefined : urls.get(cover.id);
        const here = note.id === currentId;
        return (
          <button
            key={note.id}
            type="button"
            role="radio"
            aria-checked={here}
            className="k-picker__row"
            data-testid="picker-row"
            data-note-id={note.id}
            // Отмеченная строка не нажимается: переносить некуда, она и есть «здесь».
            disabled={here || busy}
            onClick={() => onPick(note.id)}
          >
            <span className="k-picker__cover">
              {url !== undefined && <img src={url} alt="" decoding="async" />}
            </span>
            <span className="k-picker__line">
              <span className="k-mono">{formatFull(note.happened_on)}</span>
              {/* ТЕКСТ ЗАМЕТКИ — ТРЕТИЙ РАЗЛИЧИТЕЛЬ, и он нужен: две миниатюры стерни похожи
                  как две капли, а даты одной поездки совпадают. Клэмп в одну строку: здесь
                  опознают заметку, а не читают её. Заметка без слов остаётся без этой части —
                  за неё говорят кадр и дата. */}
              {note.note.trim() !== "" && (
                <span className="k-picker__text">{note.note.trim()}</span>
              )}
              {own.length > 0 && (
                <span className="k-mono k-hint">{T.markers.photosCount(own.length)}</span>
              )}
              {/* Знак места — тот же флажок, что на карте и на чипе карточки: заметка с местом
                  и заметка без него различаются формой, а не догадкой. */}
              {hasPlace(note) && (
                <span className="k-picker__place" data-testid="picker-place">
                  <Glyph d={glyph.flag} size={16} stroke={1.5} />
                </span>
              )}
            </span>
            {here && (
              <span className="k-picker__here" data-testid="picker-here">
                {T.markers.photoIsHere}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
